import React, { useState, useRef, useMemo } from 'react';
import { useRegisterTopEscapeClose } from '../../hooks/useEscapeCloseStack';
import { Download, Upload, X, Loader2 } from 'lucide-react';
import { extractVipContactsImport } from '../../services/vipContactsImportService';
import { createClient } from '../../services/clientsService';

function isValidEmail(s) {
  if (!s || typeof s !== 'string') return false;
  const e = s.trim().toLowerCase();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
}

export default function VipContactsImportModal({
  open,
  onClose,
  brokerUid,
  displayName,
  existingClients,
  t,
  language = 'fr',
  onImported,
}) {
  const [step, setStep] = useState('pick');
  const [fileLabel, setFileLabel] = useState('');
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);
  const inputRef = useRef(null);

  const reset = () => {
    setStep('pick');
    setFileLabel('');
    setLoading(false);
    setCommitting(false);
    setError('');
    setRows([]);
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  useRegisterTopEscapeClose(handleClose, open);

  const existingEmails = useMemo(
    () =>
      new Set(
        (existingClients || []).map((c) => String(c.email || '').trim().toLowerCase()).filter(Boolean)
      ),
    [existingClients]
  );

  const rowMetas = useMemo(
    () =>
      rows.map((r) => {
        const em = String(r.email || '').trim().toLowerCase();
        const ok = isValidEmail(r.email);
        const exists = ok && existingEmails.has(em);
        const canCommit = r.selected && ok && !exists;
        return { ok, exists, canCommit };
      }),
    [rows, existingEmails]
  );

  const hasCommitable = useMemo(() => rowMetas.some((m) => m.canCommit), [rowMetas]);

  const pickFile = async (file) => {
    if (!file) return;
    setError('');
    setFileLabel(file.name);
    setLoading(true);
    try {
      const b64 = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => {
          const res = String(r.result || '');
          const idx = res.indexOf('base64,');
          resolve(idx >= 0 ? res.slice(idx + 7) : res);
        };
        r.onerror = () => reject(new Error('read failed'));
        r.readAsDataURL(file);
      });
      const data = await extractVipContactsImport(b64, file.name, language === 'en' ? 'en' : 'fr');
      const list = Array.isArray(data.contacts) ? data.contacts : [];
      if (list.length === 0) {
        setError(t('clients.importVipEmpty'));
        return;
      }
      setRows(
        list.map((c, i) => {
          const email = String(c.email || '').trim().toLowerCase();
          return {
            rowKey: `${email}-${i}`,
            name: typeof c.name === 'string' ? c.name : '',
            email,
            address: typeof c.address === 'string' ? c.address : '',
            notes: typeof c.notes === 'string' ? c.notes : '',
            selected: !existingEmails.has(email),
            exists: existingEmails.has(email),
          };
        })
      );
      setStep('preview');
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  };

  const updateRow = (i, patch) => {
    setRows((prev) => prev.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  };

  const handleCommit = async () => {
    if (!brokerUid || !rows.length) return;
    const toAdd = rows.filter((r, i) => rowMetas[i]?.canCommit);
    if (toAdd.length === 0) return;
    setCommitting(true);
    setError('');
    try {
      for (const r of toAdd) {
        await createClient(brokerUid, {
          name: r.name,
          email: r.email,
          address: r.address,
          notes: r.notes || '',
          brokerDisplayName: displayName || '',
          sendPocketDeals: true,
          skipAutoInvite: true,
          pocketDealsOptInSource: 'import',
        });
      }
      onImported?.();
      handleClose();
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setCommitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[230] rentalyzer-modal-overlay rentalyzer-modal-overlay-nested flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h2 className="text-lg font-semibold text-gray-900">{t('clients.importVipTitle')}</h2>
          <button type="button" onClick={handleClose} className="p-2 rounded-lg hover:bg-gray-100" aria-label={t('cancel')}>
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 overflow-y-auto flex-1">
          {step === 'pick' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-700">{t('clients.importVipAiHint')}</p>
              <div className="rounded-lg border border-slate-200 bg-slate-50/80 px-3 py-3 text-sm text-gray-700 space-y-2">
                <p className="text-xs font-semibold text-gray-800">{t('clients.importVipFormatsTitle')}</p>
                <ul className="list-disc list-outside ms-4 space-y-1">
                  <li>{t('clients.importVipFormatExcel')}</li>
                  <li>{t('clients.importVipFormatSheets')}</li>
                  <li>{t('clients.importVipFormatWord')}</li>
                  <li>{t('clients.importVipFormatCsv')}</li>
                </ul>
                <p className="text-xs text-gray-600 pt-1">{t('clients.importVipAddressNote')}</p>
              </div>
              <input
                ref={inputRef}
                type="file"
                accept=".csv,.txt,.xlsx,.xls,.docx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                className="hidden"
                onChange={(e) => pickFile(e.target.files?.[0])}
              />
              <div className="flex flex-wrap gap-2">
                <a
                  href="/templates/vip-contacts-import-template.xlsx"
                  download="rentalyzer-vip-contacts-import-example.xlsx"
                  className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-800 rounded-lg hover:bg-gray-50"
                >
                  <Download className="w-5 h-5" />
                  {t('clients.importVipDownloadTemplate')}
                </a>
                <button
                  type="button"
                  disabled={loading}
                  onClick={() => inputRef.current?.click()}
                  className="flex items-center gap-2 px-4 py-2 border border-violet-600 text-violet-700 rounded-lg hover:bg-violet-50 disabled:opacity-50"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
                  {loading ? t('clients.importVipAnalyzing') : t('clients.importPickFile')}
                </button>
              </div>
              {fileLabel && <p className="text-xs text-gray-500">{fileLabel}</p>}
              {error && <p className="text-sm text-red-600">{error}</p>}
            </div>
          )}
          {step === 'preview' && (
            <div className="space-y-3">
              <p className="text-sm text-gray-600">{t('clients.importVipPreviewHelp')}</p>
              <div className="overflow-x-auto border rounded-lg max-h-[50vh] overflow-y-auto">
                <table className="w-full min-w-[640px] text-sm">
                  <thead className="bg-gray-50 text-left sticky top-0">
                    <tr>
                      <th className="p-2 w-10"> </th>
                      <th className="p-2">{t('clients.importVipColName')}</th>
                      <th className="p-2 min-w-[11rem]">{t('clients.importVipColEmail')}</th>
                      <th className="p-2 min-w-[8rem]">{t('clients.importVipColAddress')}</th>
                      <th className="p-2 min-w-[8rem]">{t('clients.importVipColNotes')}</th>
                      <th className="p-2 w-36">{t('clients.importVipColStatus')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, i) => {
                      const meta = rowMetas[i];
                      return (
                        <tr key={row.rowKey} className="border-t border-gray-100 align-top">
                          <td className="p-2">
                            <input
                              type="checkbox"
                              checked={row.selected}
                              disabled={meta.exists}
                              onChange={(e) => updateRow(i, { selected: e.target.checked })}
                            />
                          </td>
                          <td className="p-2">
                            <input
                              className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                              value={row.name}
                              onChange={(e) => updateRow(i, { name: e.target.value })}
                            />
                          </td>
                          <td className="p-2">
                            <input
                              className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                              value={row.email}
                              onChange={(e) => updateRow(i, { email: e.target.value.trim().toLowerCase() })}
                            />
                          </td>
                          <td className="p-2">
                            <input
                              className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                              value={row.address}
                              onChange={(e) => updateRow(i, { address: e.target.value })}
                              placeholder={t('clients.importVipAddressPlaceholder')}
                            />
                          </td>
                          <td className="p-2">
                            <textarea
                              className="w-full border border-gray-300 rounded px-2 py-1 text-xs min-h-[2.5rem]"
                              value={row.notes}
                              onChange={(e) => updateRow(i, { notes: e.target.value.slice(0, 500) })}
                              rows={2}
                            />
                          </td>
                          <td className="p-2 text-xs">
                            {meta.exists && <span className="text-amber-800">{t('clients.importVipExists')}</span>}
                            {!meta.exists && !meta.ok && row.selected && (
                              <span className="text-red-700">{t('clients.importVipBadEmail')}</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {error && <p className="text-sm text-red-600">{error}</p>}
              <div className="flex flex-wrap gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setStep('pick')}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-800 hover:bg-gray-50"
                >
                  {t('clients.importVipBack')}
                </button>
                <button
                  type="button"
                  disabled={committing || !hasCommitable}
                  onClick={handleCommit}
                  className="px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50"
                >
                  {committing ? '…' : t('clients.importVipCommit')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
